import { buttonVariants, HapticSurface } from "@pax/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { EntryCurtain } from "@/components/EntryCurtain";
import { gameQuery, joinGame } from "@/features/games/queries";
import { PlayScreen } from "@/features/play/components/PlayScreen";
import { WebPlayPlatform } from "@/features/play/platform";
import { isAccountRequired, isNotFound } from "@/lib/api";

/** How often someone waiting to be let in checks whether they have been. */
const ASKING_CHECK_MS = 5_000;

/**
 * The game's link is its invitation: opening it as someone new joins a multiplayer game when its
 * `whoCanJoin` lets them in, and otherwise asks to. A singleplayer game takes nobody new. A guest
 * can't join someone else's game, so they get an invitation to create an account; upgrading reloads
 * the route, which joins.
 */
export const Route = createFileRoute("/_authenticated/play/$gameId")({
  loader: async ({ context: { queryClient }, params: { gameId }, location }) => {
    try {
      return {
        entry: "member" as const,
        title: (await queryClient.query(gameQuery(gameId))).title,
      };
    } catch (error) {
      if (!isNotFound(error)) throw error;
    }
    try {
      const joined = await joinGame(queryClient, gameId);
      return joined.status === "joined"
        ? { entry: "member" as const, title: joined.game.title }
        : { entry: joined.status, title: joined.title };
    } catch (error) {
      // Guests play on their own, so joining someone else's game needs an account.
      if (isAccountRequired(error)) {
        throw redirect({ to: "/create-account", search: { next: location.href } });
      }
      throw error;
    }
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title ? `${loaderData.title} · Pax.co` : "Game · Pax.co" }],
  }),
  pendingComponent: EnteringGame,
  component: PlayPage,
});

function PlayPage() {
  const { gameId } = Route.useParams();
  const { entry, title } = Route.useLoaderData();
  if (entry === "asking") return <AskingToJoin gameId={gameId} title={title} />;
  if (entry === "singleplayer") return <SingleplayerGame title={title} />;
  return (
    <WebPlayPlatform>
      <PlayScreen gameId={gameId} />
    </WebPlayPlatform>
  );
}

/** Someone opened the link to a game only its owner plays. */
function SingleplayerGame({ title }: { title: string }) {
  return (
    <main className="mx-auto flex max-w-xl flex-col items-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">{title} is singleplayer</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Only the person playing it can open it. They can make it multiplayer to play together.
      </p>
      <Link to="/" className={`mt-6 ${buttonVariants({ variant: "outline" })}`}>
        Browse scenarios
        <HapticSurface />
      </Link>
    </main>
  );
}

/**
 * Someone the game's link didn't let straight in, waiting for a member who manages joining to let
 * them in. It checks now and then, and opens the game once they're in.
 */
function AskingToJoin({ gameId, title }: { gameId: string; title: string }) {
  const queryClient = useQueryClient();
  const router = useRouter();
  useEffect(() => {
    const timer = setInterval(() => {
      void joinGame(queryClient, gameId).then(joined => {
        if (joined.status !== "asking") void router.invalidate();
      });
    }, ASKING_CHECK_MS);
    return () => clearInterval(timer);
  }, [gameId, queryClient, router]);
  return (
    <main className="mx-auto flex max-w-xl flex-col items-center px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">You asked to join {title}</h1>
      <p className="shimmer-text mt-3 text-sm">Waiting for someone in the game to let you in</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Keep this open, and the game opens once you're in.
      </p>
      <Link to="/" className={`mt-6 ${buttonVariants({ variant: "outline" })}`}>
        Browse scenarios
        <HapticSurface />
      </Link>
    </main>
  );
}

/**
 * The same curtain the game screen raises, before the game is known. It carries
 * no title or cover yet, so those fade in rather than replacing a placeholder.
 */
function EnteringGame() {
  return (
    <div className="relative h-full bg-black">
      <EntryCurtain phase="connecting" />
    </div>
  );
}
